import * as API from 'constants/api';
import { useAuthSelector, useGlobalSelector } from 'stores';
import {
  IHttpMutateHelperProps,
  ILoginInput,
  ILoginResponse,
  IResetPasswordDrupalInput,
  IRegisterCheckResponse,
  IRegisterDrupalInput,
  IRegisterInput,
  IUserLoginInput,
  IRecoverPasswordDrupalResponse,
  IUpdatePasswordDrupalInput,
  IEmailCheckResponse,
} from 'types/httpClient';
import { ISession, IUser, IUserToken } from 'types/user';
import { logApiError } from 'utils/api';

export interface ISSOLoginInput {
  external_token: string;
  session_cookie?: string;
  user_data?: {
    sub: string;
    name: string;
    preferred_username: string;
    email: string;
    email_verified: boolean;
    uuid: string;
    field_name: string;
    field_surname: string;
    field_role: string;
  };
}

const useAuth = () => {
  const { showLoader, hideLoader } = useGlobalSelector();
  const { tokens, storeTokens, handleLogout } = useAuthSelector();

  const logout = async () => {
    const drupalBase =
      process.env.REACT_APP_HOST || 'https://backoffice.fla-plus.it';

    handleLogout(); // reset store + rimuove localStorage

    // Redirect diretto del browser a Drupal: stesso dominio dei cookie → Drupal invalida la sessione
    window.location.replace(`${drupalBase}/user/logout`);
  };

  const login = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<ILoginResponse, ILoginInput>): Promise<
    ILoginResponse | null | { errorCode: number; errorMessage: string }
  > => {
    try {
      useLoader && showLoader();

      const response = await fetch(API.LOGIN, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json();
        return {
          errorCode: response.status || 400,
          errorMessage: err.errorMessage || '',
        };
      }
      const { data } = await response.json();
      setData?.(data);

      console.log('🚀 ~ login ~ body:', body);
      console.log('🚀 ~ login ~ data:', data);

      // 2. Fai login anche con cookie per i wizard
      const cookieLoginResponse = await fetch(
        `${process.env.REACT_APP_DRUPAL_BASE_URL}/user/login?_format=json`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: body?.username,
            pass: body?.password,
          }),
        }
      );

      if (!cookieLoginResponse.ok) {
        return {
          errorCode: 401,
          errorMessage: 'Cookie authentication failed',
        };
      }

      const cookieData = await cookieLoginResponse.json();

      return {
        ...data,
        csrf_token: cookieData.csrf_token,
        logout_token: cookieData.logout_token,
      };
    } catch (err) {
      logApiError(err, 'login');
      return null;
    } finally {
      useLoader && hideLoader();
    }
  };

  const refreshTokens = async (
    newTokens?: IUserToken
  ): Promise<IUserToken | void> => {
    const refreshToken = newTokens?.refresh_token || tokens?.refresh_token;

    try {
      if (!refreshToken) return;

      // Se il token è convertito da CSRF, non fare refresh
      if (tokens?.converted_from_csrf || tokens?.cookie_mode) {
        return tokens;
      }

      const response = await fetch(API.REFRESH_TOKEN, {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
      if (response && response.ok) {
        const { data: refreshedTokens } = await response.json();
        const updatedUserTokens: IUserToken = {
          ...refreshedTokens,
        };
        storeTokens(updatedUserTokens);
        return updatedUserTokens;
      } else {
        handleLogout();
      }
    } catch {
      handleLogout();
    }
  };

  const getUserInfo = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<IUser, IUserLoginInput>): Promise<IUser | null> => {
    try {
      useLoader && showLoader();

      if (!body?.access_token) return null;

      const response = await fetch(API.OAUTH_USER_INFO, {
        headers: {
          Authorization: 'Bearer ' + body.access_token,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const data = await response.json();
      setData?.(data);
      return data;
    } catch (err) {
      logApiError(err, 'get-user-info');
      return null;
    } finally {
      useLoader && hideLoader();
    }
  };

  const register = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<
    ILoginResponse,
    IRegisterDrupalInput
  >): Promise<ILoginResponse | null> => {
    try {
      useLoader && showLoader();

      const response = await fetch(API.REGISTER_DRUPAL, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const { data } = await response.json();
      setData?.(data);
      return data;
    } catch (err) {
      logApiError(err, 'register-drupal');
      return null;
    } finally {
      useLoader && hideLoader();
    }
  };

  const ssoLogin = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<ILoginResponse, ISSOLoginInput>): Promise<
    | (ILoginResponse & { user?: ISSOLoginInput['user_data'] })
    | null
    | { errorCode: number; errorMessage: string }
  > => {
    try {
      useLoader && showLoader();

      const response = await fetch(API.SSO_LOGIN, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const apiResponse = await response.json();
        setData?.(apiResponse.data);
        // Per SSO, restituiamo l'intera risposta per preservare user data
        return { ...apiResponse.data, user: apiResponse.user };
      }

      const err = await response.json();
      return {
        errorCode: response.status || 400,
        errorMessage: err.errorMessage || 'SSO authentication failed',
      };
    } catch (err) {
      logApiError(err, 'sso-login');
      return null;
    } finally {
      useLoader && hideLoader();
    }
  };

  const emailCheck = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<
    IEmailCheckResponse,
    IResetPasswordDrupalInput
  >): Promise<IEmailCheckResponse> => {
    try {
      useLoader && showLoader();

      const response = await fetch(API.EMAIL_CHECK_DRUPAL, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const apiResponse = await response.json();
      setData?.(apiResponse.data);

      return {
        status: apiResponse.errorMessage ? 404 : 200,
        userMail: 'found',
      };
    } catch (err) {
      logApiError(err, 'email-check');
      return { status: 404, userMail: 'found' };
    } finally {
      useLoader && hideLoader();
    }
  };

  const resetPassword = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<
    IRecoverPasswordDrupalResponse,
    IResetPasswordDrupalInput
  >): Promise<IRecoverPasswordDrupalResponse> => {
    try {
      useLoader && showLoader();

      const response = await fetch(API.RESET_PASSWORD_DRUPAL, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const apiResponse = await response.json();
      setData?.(apiResponse.data);

      return apiResponse.errorMessage
        ? { status: 404, message: 'Email not found' }
        : {
            status: 200,
            message: `Password reset requested for ${body?.email}.`,
          };
    } catch (err) {
      logApiError(err, 'reset-password-drupal');
      return { status: 404, message: 'Email not found' };
    } finally {
      useLoader && hideLoader();
    }
  };

  const updateRecoveryPassword = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<
    IRecoverPasswordDrupalResponse,
    IUpdatePasswordDrupalInput
  >): Promise<IRecoverPasswordDrupalResponse> => {
    try {
      useLoader && showLoader();

      const response = await fetch(API.UPDATE_PASSWORD_DRUPAL, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const apiResponse = await response.json();
      setData?.(apiResponse.data);

      return apiResponse.errorMessage
        ? { status: 404, message: 'Link not valid' }
        : {
            status: 200,
            message: `Password reset requested for ${body?.email}.`,
          };
    } catch (err) {
      logApiError(err, 'update-password-drupal');
      return { status: 404, message: 'Link not valid' };
    } finally {
      useLoader && hideLoader();
    }
  };

  const registerCheck = async ({
    useLoader,
    setData,
    body,
  }: IHttpMutateHelperProps<
    IRegisterCheckResponse,
    IRegisterInput
  >): Promise<IRegisterCheckResponse | null> => {
    try {
      useLoader && showLoader();

      const response = await fetch(API.REGISTER_CHECK, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const apiResponse = await response.json();

      if (response.status === 404 && apiResponse.errorMessage === 'Not Found') {
        return null;
      }

      setData?.(apiResponse.data);
      return apiResponse.data;
    } catch {
      return null;
    } finally {
      useLoader && hideLoader();
    }
  };

  /**
   * Controlla se il browser ha già un cookie di sessione Drupal attivo.
   * La chiamata avviene tramite il proxy serverless /api/auth/checkSession
   * (stesso dominio → nessun CORS) che inoltra i cookie a Drupal server-side.
   * Non modifica lo store: la chiamante decide se e come salvare la sessione.
   */
  const checkDrupalSession = async (): Promise<ISession | null> => {
    try {
      // Chiamata same-origin: il browser invia i cookie automaticamente
      const response = await fetch(API.CHECK_SESSION, {
        method: 'GET',
        credentials: 'include', // invia i cookie Drupal al nostro proxy
      });

      if (!response.ok) return null;

      const { data } = await response.json();
      if (!data || !data.uid) return null;

      // csrf_token è già restituito da checkSession — nessuna fetch aggiuntiva
      const csrfToken = data.csrf_token;
      console.log('🚀 CSRF token:', csrfToken);

      const res = await fetch(API.RECOVERY_TOKEN_DRUPAL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csrf_token: csrfToken,
          session_cookie: document.cookie,
        }),
      });
      console.log('🚀 ~ checkDrupalSession ~ res:', res);
      const accessToken = await res.json();
      console.log('🚀 ~ checkDrupalSession ~ accessToken:', accessToken);

      // // Recupera l'access token OAuth usando la sessione attiva
      // const tokenRes = await fetch(
      //   `${process.env.REACT_APP_DRUPAL_BASE_URL}/oauth/token`,
      //   {
      //     method: 'POST',
      //     headers: {
      //       'Content-Type': 'application/x-www-form-urlencoded',
      //       Cookie: document.cookie, // session cookie del browser
      //     },
      //     body: new URLSearchParams({
      //       grant_type: 'password', // oppure 'authorization_code' se usi SSO
      //       client_id: process.env.REACT_APP_OAUTH_CLIENT_ID || '',
      //       client_secret: process.env.REACT_APP_OAUTH_CLIENT_SECRET || '',
      //       // username/password non necessari se il grant usa la sessione
      //     }),
      //   }
      // );

      // Usalo nelle chiamate
      // const result = await fetch(
      //   'https://tuosito.it/api/endpoint?_format=json',
      //   {
      //     method: 'GET',
      //     headers: {
      //       'X-CSRF-Token': csrfToken,
      //       'Content-Type': 'application/json',
      //     },
      //     credentials: 'include',
      //   }
      // );

      // const user: IUser = {
      //   sub: String(data.uid),
      //   name: data.name || '',
      //   preferred_username: data.name || '',
      //   email: data.mail || '',
      //   email_verified: true,
      //   profile: '',
      //   locale: '',
      //   zoneinfo: '',
      //   updated_at: '',
      //   uuid: data.uuid || '',
      //   field_name: data.field_name || '',
      //   field_surname: data.field_surname || '',
      //   field_telephone: data.field_telephone || '',
      //   field_company: data.field_company || '',
      //   field_vat_number: data.field_vat_number || '',
      //   field_role: data.field_role || 'USER',
      // };

      const name = `${data?.attrs.field_name} ${data?.attrs.field_surname}`;
      const user: IUser = {
        sub: data.attrs.sub || data.uid || 'sso_' + Date.now(),
        name: name || 'SSO User',
        preferred_username:
          data.attrs.preferred_username || data.attrs.name || 'sso_user',
        email: data.attrs.mail,
        email_verified:
          data.attrs.email_verified !== undefined
            ? data.attrs.email_verified
            : true,
        // uuid: data.attrs.uuid || data.attrs.sub || 'sso_' + Date.now(),
        uuid: data.uuid || '',
        field_name: data.attrs.field_name || '',
        field_surname: data.attrs.field_surname || '',
        field_company: data.attrs?.field_company || '',
        field_vat_number: data.attrs?.field_vat_number || '',
        field_role: data.attrs.field_role || '',
        profile:
          data.attrs.profile ||
          `${process.env.REACT_APP_DRUPAL_BASE_URL}/user/${data.uid}`,
        locale: '',
        zoneinfo: '',
        updated_at: '',
      };

      const tokens: IUserToken = {
        access_token: accessToken?.data?.access_token,
        refresh_token: accessToken?.data?.refresh_token || '',
        cookie_mode: true,
        csrf_token: csrfToken,
        logout_token: data.logout_token || '',
      };

      return { user, tokens };
    } catch {
      return null;
    }
  };

  return {
    login,
    ssoLogin,
    register,
    refreshTokens,
    getUserInfo,
    registerCheck,
    resetPassword,
    updateRecoveryPassword,
    emailCheck,
    checkDrupalSession,
    logout,
  };
};

export { useAuth };
